import React, { useRef, useState, useEffect, useMemo } from 'react';
import { CopyOutlined, DeleteOutlined, EditOutlined, SearchOutlined, SmallDashOutlined } from '@ant-design/icons';
import { CalendarOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import { Button, Checkbox, Dropdown, Input, Menu, Space, Table, Tooltip } from 'antd';
import { Link } from "react-router-dom";
import firebase from '../../config/firebase';
import { doc, getDoc } from "firebase/firestore";
import { useSelector } from 'react-redux';
import Stack from '@mui/material/Stack';
import { Chip, Badge } from '@mui/material';
import EditNotificationsOutlinedIcon from '@mui/icons-material/EditNotificationsOutlined';
import { format } from 'date-fns';
import DatePickerMenu from '../dataPicker/dataPickerMenu';
import DialogFileDriver from '../dialog-fila-driver/dialog-fila-driver';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ModalFilter from '../ModalFilter/ModalFilter';
import dayjs from 'dayjs';
import './data-frete-table.css';
require('firebase/auth');

const DriverBadge = ({ recordId }) => {
  const [driversInFila, setDriversInFila] = useState([]);
  const [status, setStatus] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      const db = firebase.firestore();

      const freightDoc = await db.collection('freight').doc(recordId).get();
      if (freightDoc.exists) {
        setStatus(freightDoc.data().status.describe);
      }

      const listDriverInFila = [];
      const result = await db.collection('freight').doc(recordId).collection('queue').get();
      result.docs.forEach(doc => {
        listDriverInFila.push({ id: doc.id, ...doc.data() });
      });
      setDriversInFila(listDriverInFila);
    };

    fetchData();
  }, [recordId]);

  if (status === 'Finalizado' || status === 'Contratado') {
    return (
      <EditNotificationsOutlinedIcon />
    );
  }

  return (
    <Link to={'#' + recordId}>
      {driversInFila.length > 0 ? (
        <Badge badgeContent={driversInFila.length} color="success">
          <EditNotificationsOutlinedIcon color='primary' />
        </Badge>
      ) : (
        <EditNotificationsOutlinedIcon color='primary' />
      )}
    </Link>
  );
};

const statusPriority = {
  "Entregue": 1,
  "Em trânsito": 2,
  "Contratado": 3,
  "Pendente de contratação": 4,
  "Pendente de validação": 5,
  "Finalizado": 6,
};

const DataFreteTable = ({ cleanFilter, openFilter, onCleanFilterHandled, onOpenFilterHandled }) => {
  const db = firebase.firestore();

  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [openModalFilter, setOpenModalFilter] = useState(false);
  const [driversInFila, setDriversInFila] = useState([]);
  const [datas, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dateRange, setDateRange] = useState([]);
  const unsubscribeQueueRef = useRef(null);
  const unsubscribeFreightRef = useRef(null);
  const hasLoadedRef = useRef(false);
  const [columnFilters, setColumnFilters] = useState({});
  const [statusSearchText, setStatusSearchText] = useState('');
  const statusFilterRef = useRef(null);

  const [pageSize, setPageSize] = useState(10);
  const [current, setCurrent] = useState(1);

  const storageKey = (uid, key) => `${uid}:${key}`;

  const handleDateRangeChange = (dates) => {
    setDateRange(dates ? [dates[0], dates[1]] : []);
  };

  const searchInput = useRef(null);

  const user = useSelector(state => state.user);

  const getDriversQueue = (id) => {
    const db = firebase.firestore();

    // Limpa o listener anterior se existir
    if (unsubscribeQueueRef.current) {
      unsubscribeQueueRef.current();
      unsubscribeQueueRef.current = null;
    }

    // Cria o listener com onSnapshot
    const unsubscribe = db.collection('freight').doc(id).collection('queue')
      .onSnapshot((snapshot) => {
        const listDriverInFila = [];
        snapshot.docs.forEach(doc => {
          listDriverInFila.push({
            id: doc.id,
            ...doc.data()
          });
        });
        setDriversInFila(listDriverInFila);
        handleOpen();
      }, (error) => {
        console.error(error);
      });

    unsubscribeQueueRef.current = unsubscribe;
  };

  const fetchData = () => {
    // Limpa o listener anterior se existir
    if (unsubscribeFreightRef.current) {
      unsubscribeFreightRef.current();
      unsubscribeFreightRef.current = null;
    }

    // Define loading como true ao iniciar
    setLoading(true);

    let query;

    // Cria a query baseada nas condições do usuário
    if (user.viewALL) {
      query = firebase.firestore().collection("freight");
    } else {
      if (user?.uidShipperAffiliates && Array.isArray(user.uidShipperAffiliates)) {
        const affiliateUids = Array.isArray(user?.uidShipperAffiliates) ? user.uidShipperAffiliates : [];
        const primaryUid = user?.uidShipper ? [user.uidShipper] : [];
        const uids = Array.from(new Set([...affiliateUids, ...primaryUid]));

        if (uids.length > 1) {
          query = firebase.firestore().collection("freight")
            .where('clientPayment.uid', 'in', uids);
        }
        else {
          query = firebase.firestore().collection("freight")
            .where('clientPayment.uid', '==', user.uidShipper);
        }
      }
      else {
        query = firebase.firestore().collection("freight")
          .where('clientPayment.uid', '==', user.uidShipper);
      }
    }

    // Cria o listener com onSnapshot
    const unsubscribe = query.onSnapshot(async (snapshot) => {
      const documents = await Promise.all(snapshot.docs.map(async (docSnap) => {
        const docData = docSnap.data();
        const uid = docData?.freight?.getDriverFreight?.uidDriver;

        // Busca a subcoleção stopping_points
        let stoppingPoints = [];

        try {
          const stoppingPointsSnapshot = await db.collection('freight').doc(docSnap.id).collection('stopping_points').get();
          stoppingPoints = stoppingPointsSnapshot.docs.map(spDoc => ({ id: spDoc.id, ...spDoc.data() }));
        }
        catch (error) {
          console.error("Erro ao buscar stopping_points:", error);
        }

        // Se tiver uidDriver, tenta buscar o nome do motorista
        if (uid) {
          try {
            const driverRef = doc(db, "drivers_users", uid);
            const driverSnap = await getDoc(driverRef);

            if (driverSnap.exists()) {
              const driverData = driverSnap.data();
              return {
                id: docSnap.id,
                ...docData,
                stopping_points: stoppingPoints,
                freight: {
                  ...docData.freight,
                  getDriverFreight: {
                    ...docData.freight.getDriverFreight,
                    name: driverData.name || "Nome não encontrado",
                  },
                },
              };
            } else {
              return {
                id: docSnap.id,
                ...docData,
                stopping_points: stoppingPoints,
                getDriverFreight: {
                  ...docData.getDriverFreight,
                  name: "Motorista não encontrado",
                },
              };
            }
          } catch (error) {
            console.error("Erro ao buscar motorista:", error);
            return {
              id: docSnap.id,
              ...docData,
              stopping_points: stoppingPoints,
              getDriverFreight: {
                ...docData.getDriverFreight,
                name: "Erro ao buscar nome",
              },
            };
          }
        }

        // Se não tiver uidDriver, retorna como está
        return { id: docSnap.id, ...docData, stopping_points: stoppingPoints };
      }));

      setData(documents);
      setLoading(false);
    }, (error) => {
      console.error("Erro ao buscar dados:", error);
      setLoading(false);
    });

    unsubscribeFreightRef.current = unsubscribe;
  };

  useEffect(() => {
    if (user) {
      fetchData();
    }

    // Cleanup quando o componente desmontar ou user mudar
    return () => {
      if (unsubscribeFreightRef.current) {
        unsubscribeFreightRef.current();
        unsubscribeFreightRef.current = null;
      }
    };
  }, [user]);

  // Cleanup adicional quando o componente desmontar (backup)
  useEffect(() => {
    return () => {
      if (unsubscribeQueueRef.current) {
        unsubscribeQueueRef.current();
      }
      if (unsubscribeFreightRef.current) {
        unsubscribeFreightRef.current();
      }
    };
  }, []);

  useEffect(() => { // load do localStorage com base na filtragem
    if (!user?.uid) return;

    const savedDateRange = localStorage.getItem(
      storageKey(user.uid, 'freightDateRangeFilter')
    );

    const savedColumnFilters = localStorage.getItem(
      storageKey(user.uid, 'freightColumnFilters')
    );

    if (savedColumnFilters) {
      setColumnFilters(JSON.parse(savedColumnFilters));
    }
    else {
      setColumnFilters({});
    }

    if (savedDateRange) {
      const parsed = JSON.parse(savedDateRange);

      const normalized =
        Array.isArray(parsed) && parsed.length === 2
          ? parsed.map(d => dayjs(d))
          : null;

      setDateRange(normalized);
      setSearchedColumn('dateColeta');
    }
    else {
      setDateRange(null);
    }

    hasLoadedRef.current = true;
  }, [user?.uid]);

  useEffect(() => { // Save do localStorage com base na filtragem
    if (!hasLoadedRef.current) return;
    if (!user?.uid) return;

    if (dateRange) {
      localStorage.setItem(
        storageKey(user.uid, 'freightDateRangeFilter'),
        JSON.stringify(dateRange)
      );
    }
    else {
      localStorage.removeItem(
        storageKey(user.uid, 'freightDateRangeFilter')
      );
    }

    if (columnFilters && Object.keys(columnFilters).length) {
      localStorage.setItem(
        storageKey(user.uid, 'freightColumnFilters'),
        JSON.stringify(columnFilters)
      );
    }
    else {
      localStorage.removeItem(
        storageKey(user.uid, 'freightColumnFilters')
      );
    }
  }, [user?.uid, dateRange, columnFilters]);

  useEffect(() => {
    if (cleanFilter) {
      cleanFilters()
      onCleanFilterHandled?.();
    }
  }, [cleanFilter]);

  useEffect(() => {
    if (openFilter) {
      setOpenModalFilter(true);
      onOpenFilterHandled?.();
    }
  }, [openFilter]);


  const handleOpen = () => {
    setOpenModal(true);
  };

  const handleClose = () => {
    setOpenModal(false);
    // Limpa o listener quando o modal for fechado
    if (unsubscribeQueueRef.current) {
      unsubscribeQueueRef.current();
      unsubscribeQueueRef.current = null;
    }
  };

  const handleSearch = (selectedKeys, confirm, columnKey) => {
    confirm();
    // setSearchText(selectedKeys[0]?.toString() ?? '');
    // setSearchedColumn(columnKey);
    setColumnFilters((prev) => ({
      ...prev,
      [columnKey]: columnKey === 'freight.valueFreightage' || columnKey === 'driver.valueNegotiated' ? moneyToInt(selectedKeys) : selectedKeys.toString() ?? '',
    }));
  };

  const handleSearchDate = (confirm) => {
    confirm();
    setSearchText('');
    setSearchedColumn('dateColeta');
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    // setSearchText('');
    setColumnFilters(prev => {
      const updatedFilters = { ...prev };
      delete updatedFilters[searchedColumn];
      return updatedFilters;
    });
  };

  const handleResetDate = (clearFilters) => {
    clearFilters();
    setDateRange([]);
  };

  const deleteEmb = async (id) => {
    const result = window.confirm("Deseja mesmo excluir ?");
    if (!result) return;

    try {
      // Excluir o frete
      await firebase.firestore()
        .collection("freight")
        .doc(id)
        .delete();

      alert("Frete excluído com sucesso!");

      // Buscar pagamentos relacionados
      const snapshot = await firebase.firestore()
        .collection("payment")
        .where("idFreight", "==", id)
        .get();

      // Deletar cada pagamento encontrado
      const batch = firebase.firestore().batch();
      snapshot.forEach((doc) => {
        batch.delete(doc.ref);
      });
      await batch.commit();

      // Atualizar dados
      fetchData();
    } catch (error) {
      alert("Erro ao excluir o frete: " + error.message);
    }
  };

  function renderStatusColor(status) {
    const chipStyle = { minWidth: "100px", maxWidth: "150px" };
    const commonChip = (label, color) => (
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <Chip
          label={label}
          sx={{
            ...chipStyle,
            backgroundColor: color,
            color: "#F9FAFB",
            borderRadius: "6px",
            fontSize: "12px",
            fontWeight: 600,
            height: 28,
            px: 0.5,
            boxShadow:
              "0px 3px 3px rgba(0,14,51,0.25), 0px 1px 2px rgba(0,14,51,0.25)",
            border: "1px solid rgba(1,36,66,0.08)",
            "& .MuiChip-label": {
              px: 1,
            },
          }}
        />
      </Stack>
    );

    switch (status) {
      case "Contratado":
        return commonChip(status, "#059669");
      case "P. Validação":
      case "Pendente de validação":
        return commonChip("P. Validação", "#A855F7");
      case "P.Contratação":
      case "Pendente de contratação":
        return commonChip("P.Contratação", "#F97316");
      case "Em transito":
      case "Em trânsito":
        return commonChip("Em trânsito", "#16A34A");
      case "Entregue":
        return commonChip(status, "#2563EB");
      case "Finalizado":
        return commonChip(status, "#6B7280");
      case "Deslocando-se para a coleta":
        return commonChip(status, "#22C55E");
      case "Aguardando Carregamento":
        return commonChip(status, "#F59E0B");
      case "Aguardando Documentação":
        return commonChip(status, "#FBBF24");
      case "Aguardando Descarregamento":
        return commonChip(status, "#EAB308");
      default:
        return commonChip(status || "—", "#020202");
    }
  }

  function moneyToInt(value) {
    if (value == null) return 0;

    // se já for número, retorna direto
    if (typeof value === "number") {
      return Math.round(value);
    }

    // garante string
    const str = String(value);

    return Math.round(
      Number(
        str
          .replace(/\./g, '')
          .replace(',', '.')
      )
    );
  }

  function cleanFilters() {
    setColumnFilters({});
    setDateRange([]);
  };

  const handleApplyCustomFilter = (filter) => {
    // Limpar filtros existentes antes de aplicar o novo
    const newFilters = {};
    let newDateRange = null;

    // Processar cada condição do filtro
    if (filter.conditions && filter.conditions.length > 0) {
      filter.conditions.forEach(condition => {
        const { column, operator, value } = condition;

        // Tratar filtros de data separadamente
        if (column === 'firstDelivery.dateColeta') {
          if (operator === 'between' && value && Array.isArray(value) && value.length === 2) {
            const startDate = typeof value[0] === 'string' ? dayjs(value[0]) : value[0];
            const endDate = typeof value[1] === 'string' ? dayjs(value[1]) : value[1];
            newDateRange = [startDate, endDate];
          }
          else if (operator === 'equals' && value) {
            const date = typeof value === 'string' ? dayjs(value) : value;
            newDateRange = [date, date];
          }
          else if (operator === 'after' && value) {
            const date = typeof value === 'string' ? dayjs(value) : value;
            newDateRange = [date, null];
          }
          else if (operator === 'before' && value) {
            const date = typeof value === 'string' ? dayjs(value) : value;
            newDateRange = [null, date];
          }
          else if (value) {
            const date = typeof value === 'string' ? dayjs(value) : value;
            newDateRange = [date, date];
          }
        }

        // Tratar filtros de status
        else if (column === 'status.describe') {
          if (operator === 'in' && Array.isArray(value) && value.length > 0) {
            newFilters[column] = value;
          }
          else if (operator === 'equals' && value) {
            newFilters[column] = Array.isArray(value) ? value : [value];
          }
        }
        // Tratar filtros numéricos com operadores condicionais
        else if ((column === 'freight.valueFreightage' || column === 'driver.valueNegotiated') && value) {
          newFilters[column] = {
            operator,
            value: parseFloat(value) || 0
          };
        }
        // Tratar outros filtros de texto/número, Para operador 'contains' ou 'equals' em campos de texto
        else {
          if (operator === 'contains' || operator === 'equals' || operator === 'startsWith' || operator === 'endsWith') {
            newFilters[column] = value;
          }
        }
      });
    }

    // Aplicar os filtros de forma síncrona, primeiro aplicar o dateRange, depois os columnFilters
    if (newDateRange) {
      setDateRange(newDateRange);
    }

    // Aplicar os filtros de coluna, bug encontrado de column + dateRange por isso essa ordem
    setColumnFilters(newFilters);
  };

  const getColumnSearchProps = (dataIndex, type = "text", maxLength = 40) => {
    // usamos uma chave única por coluna que funciona tanto para string quanto array
    const columnKey = Array.isArray(dataIndex) ? dataIndex.join('.') : dataIndex;

    return {
      filterDropdown: ({ setSelectedKeys, selectedKeys = [], confirm, clearFilters, close }) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            ref={searchInput}
            placeholder={`Buscar ${columnKey}`}
            value={selectedKeys[0] ?? ''}
            maxLength={type === "text" ? maxLength : undefined}
            onChange={(e) => {
              let value = e.target.value ?? '';
              if (type === "number") {
                value = value.replace(/\D/g, "");
              }
              else if (type === "money") {
                value = value.replace(/\D/g, "");
                value = (Number(value) / 100).toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                });
              }
              else if (type === "text") {
                // remove dígitos — mantém letras, espaços e sinais básicos (acentos continuam)
                // tentativa mais segura usando Unicode letters; se não suportar, cai no fallback abaixo
                try {
                  value = value.replace(/[0-9]/g, ""); // remove números
                  // opcionalmente, para permitir só letras e espaços use:
                  // value = value.replace(/[^\p{L}\s'-]/gu, '');
                } catch (err) {
                  // fallback simples: remove dígitos
                  value = value.replace(/\d/g, "");
                }
              }

              setSelectedKeys(value ? [value] : []);
            }
            }
            onPressEnter={() => {
              // confirma (aplica filtro)
              handleSearch(selectedKeys, confirm, columnKey);
            }}
            style={{ marginBottom: 8, display: 'block' }}
          />

          <Space>
            <Button
              type="primary"
              onClick={() => handleSearch(selectedKeys, confirm, columnKey)}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Buscar
            </Button>

            <Button
              onClick={() => {
                if (clearFilters) {
                  handleReset(clearFilters);
                }

                // limpa estados de busca da sua tabela
                columnFilters[columnKey] && setColumnFilters(prev => {
                  const updatedFilters = { ...prev };
                  delete updatedFilters[columnKey];
                  return updatedFilters;
                });

                // recarrega os dados originais
                confirm();
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reiniciar
            </Button>

            <Button type="link" size="small" onClick={() => close()}>
              Fechar
            </Button>
          </Space>
        </div>
      ),

      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
      ),

      onFilter: (value, record) => {
        // Os filtros são aplicados manualmente em getFilteredAndSortedData
        // então sempre retornamos true aqui para evitar duplicação de filtros
        return true;
      },

      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      },

      render: (text) =>
        searchedColumn === columnKey ? (
          <Highlighter
            highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
            searchWords={[searchText]}
            autoEscape
            textToHighlight={text ? text.toString() : ''}
          />
        ) : (
          text
        ),
    };
  };

  // Normaliza qualquer valor de data para um Date somente com ano/mês/dia (sem problemas de fuso)
  const getDateOnly = (value) => {
    if (!value) return null;

    // Objeto dayjs
    if (dayjs.isDayjs && dayjs.isDayjs(value)) {
      if (!value.isValid()) return null;
      return new Date(value.year(), value.month(), value.date());
    }

    // String no formato 'YYYY-MM-DD' (como vem do Firestore)
    if (typeof value === 'string') {
      const [year, month, day] = value.split('-').map(Number);
      if (!year || !month || !day) return null;
      return new Date(year, month - 1, day);
    }

    // Objeto com método toDate (moment, dayjs, Timestamp, etc)
    if (typeof value === 'object' && typeof value.toDate === 'function') {
      const d = value.toDate();
      if (!(d instanceof Date) || isNaN(d.getTime())) return null;
      return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    }

    // Date nativo
    if (value instanceof Date) {
      if (isNaN(value.getTime())) return null;
      return new Date(value.getFullYear(), value.getMonth(), value.getDate());
    }

    return null;
  };

  // Retorna a menor date_operation do array stopping_points (formato 'YYYY-MM-DD')
  const getMinDateOperation = (stoppingPoints) => {
    if (!stoppingPoints || !Array.isArray(stoppingPoints) || stoppingPoints.length === 0) return null;
    const dates = stoppingPoints
      .map(sp => sp?.date_operation)
      .filter(Boolean);
    if (dates.length === 0) return null;
    return dates.sort()[0]; // 'YYYY-MM-DD' ordena corretamente como string
  };

  // Data de coleta: prioriza menor date_operation de stopping_points, fallback para firstDelivery
  const getDateColetaFromRecord = React.useCallback((record) => {
    const minFromStopping = getMinDateOperation(record?.stopping_points);
    return minFromStopping || record?.firstDelivery?.dateColeta;
  }, []);

  // Função para filtrar por data
  const getColumnSearchPropsDate = () => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <DatePickerMenu
          value={dateRange}
          onChange={(dates) => {
            handleDateRangeChange(dates); // Atualiza o estado do intervalo de datas
            setSelectedKeys(dates ? [dates] : []); // Armazena o intervalo de datas selecionado
          }}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearchDate(confirm)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Buscar
          </Button>
          <Button
            onClick={() => clearFilters && handleResetDate(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reiniciar
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <CalendarOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (_, record) => {
      // Os filtros são aplicados manualmente em getFilteredAndSortedData
      // então sempre retornamos true aqui para evitar duplicação de filtros
      return true;
    },
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === 'dateColeta' ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  });

  // Aplica todos os filtros exceto o filtro de status para obter dados filtrados
  const getFilteredDataWithoutStatus = () => {
    let filteredData = [...datas];

    // Aplica filtro de data
    if (dateRange && dateRange.length) {
      const [startDate, endDate] = dateRange;
      filteredData = filteredData.filter(record => {
        const dateColeta = getDateOnly(getDateColetaFromRecord(record));
        if (!dateColeta) return false;

        const start = getDateOnly(startDate);
        const end = getDateOnly(endDate);

        if (start && end && start.getTime() === end.getTime()) {
          return dateColeta.getTime() === start.getTime();
        }

        if (start && !end) {
          return dateColeta.getTime() >= start.getTime();
        }

        if (start && end) {
          return (
            dateColeta.getTime() >= start.getTime() &&
            dateColeta.getTime() <= end.getTime()
          );
        }

        return true;
      });
    }

    // Aplica filtros de colunas (exceto status.describe)
    Object.keys(columnFilters).forEach(columnKey => {
      if (columnKey === 'status.describe') return; // Pula o filtro de status

      const filterValue = columnFilters[columnKey];
      if (filterValue == null || filterValue === '') return;

      filteredData = filteredData.filter(record => {
        // Para filtros de data já aplicados acima
        if (columnKey === 'dateColeta') return true;

        const columnKeyArray = columnKey.split('.');
        let recordValue = record;

        for (const key of columnKeyArray) {
          recordValue = recordValue?.[key];
        }

        if (recordValue == null) return false;

        // Determina o tipo de filtro baseado na coluna
        const isNumber = columnKey === 'numberSerial';
        const isMoney = columnKey === 'freight.valueFreightage' || columnKey === 'driver.valueNegotiated';

        // Aplica o filtro conforme o tipo
        if (isNumber) {
          return String(recordValue).includes(String(filterValue));
        } else if (isMoney) {
          // Para valores monetários, o filterValue já está como int no columnFilters
          const filterInt = typeof filterValue === 'number' ? filterValue : moneyToInt(filterValue);
          const recordInt = typeof recordValue === 'number' ? Math.round(recordValue) : moneyToInt(recordValue);
          return String(recordInt).includes(String(filterInt));
        } else {
          // Para texto, compara case-insensitive
          return recordValue.toString().toLowerCase().includes(filterValue.toString().toLowerCase());
        }
      });
    });

    return filteredData;
  };

  // Extrai valores únicos de status dos dados filtrados (sem o filtro de status)
  const getUniqueStatusValues = () => {
    const filteredData = getFilteredDataWithoutStatus();
    const uniqueStatuses = new Set();
    filteredData.forEach(record => {
      if (record.status?.describe) {
        uniqueStatuses.add(record.status.describe);
      }
    });
    return Array.from(uniqueStatuses).sort();
  };

  const getStatusFilterProps = () => {
    const statusOptions = getUniqueStatusValues();

    return {
      filterDropdown: ({ setSelectedKeys, selectedKeys = [], confirm, clearFilters }) => {
        const filteredOptions = statusSearchText
          ? statusOptions.filter(status =>
            status.toLowerCase().includes(statusSearchText.toLowerCase())
          )
          : statusOptions;

        const handleSelectAll = (checked) => {
          if (checked) {
            setSelectedKeys(filteredOptions);
          } else {
            setSelectedKeys([]);
          }
        };

        const handleChange = (status, checked) => {
          if (checked) {
            setSelectedKeys([...selectedKeys, status]);
          } else {
            setSelectedKeys(selectedKeys.filter(key => key !== status));
          }
        };

        const allSelected = filteredOptions.length > 0 &&
          filteredOptions.every(option => selectedKeys.includes(option));

        const someSelected = filteredOptions.some(option => selectedKeys.includes(option)) && !allSelected;

        return (
          <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
            <Input
              ref={statusFilterRef}
              placeholder="Buscar..."
              value={statusSearchText}
              onChange={(e) => setStatusSearchText(e.target.value)}
              style={{ marginBottom: 8 }}
              allowClear
              onPressEnter={() => confirm()}
            />
            <div style={{ maxHeight: 250, overflowY: 'auto', marginBottom: 8 }}>
              <div style={{ marginBottom: 4 }}>
                <Checkbox
                  indeterminate={someSelected}
                  checked={allSelected}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                >
                  (Selecionar todos)
                </Checkbox>
              </div>
              {filteredOptions.map(status => (
                <div key={status} style={{ marginBottom: 4 }}>
                  <Checkbox
                    checked={selectedKeys.includes(status)}
                    onChange={(e) => handleChange(status, e.target.checked)}
                  >
                    {status === 'Pendente de validação'
                      ? 'P. Validação'
                      : status === 'Pendente de contratação'
                        ? 'P.Contratação'
                        : status}
                  </Checkbox>
                </div>
              ))}
            </div>
            <Space>
              <Button
                onClick={() => {
                  if (clearFilters) {
                    clearFilters();
                  }
                  setSelectedKeys([]);
                  setStatusSearchText('');
                  setColumnFilters(prev => {
                    const updatedFilters = { ...prev };
                    delete updatedFilters['status.describe'];
                    return updatedFilters;
                  });
                  confirm();
                }}
                size="small"
                style={{ width: 90 }}
              >
                Resetar
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  setColumnFilters(prev => ({
                    ...prev,
                    'status.describe': selectedKeys
                  }));
                  confirm();
                }}
                size="small"
                style={{ width: 90 }}
              >
                Aplicar
              </Button>
            </Space>
          </div>
        );
      },
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
      ),
      onFilter: (value, record) => {
        // Os filtros são aplicados manualmente em getFilteredAndSortedData
        // então sempre retornamos true aqui para evitar duplicação de filtros
        return true;
      },
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => statusFilterRef.current?.select(), 100);
          // Limpa a busca ao abrir o dropdown
          setStatusSearchText('');
        }
      },
    };
  };

  const columns = [
    {
      title: 'Data Coleta',
      dataIndex: ['stopping_points'],
      key: 'dateColeta',
      align: 'center',
      // width: '20%',
      filteredValue: dateRange && dateRange.length ? [dateRange] : null,
      ...getColumnSearchPropsDate(),
      sorter: (a, b) => {
        const dateA = getDateColetaFromRecord(a);
        const dateB = getDateColetaFromRecord(b);

        if (!dateA) return -1;
        if (!dateB) return 1;

        const [yearA, monthA, dayA] = dateA.split('-').map(Number);
        const [yearB, monthB, dayB] = dateB.split('-').map(Number);

        const timeA = new Date(yearA, monthA - 1, dayA).getTime();
        const timeB = new Date(yearB, monthB - 1, dayB).getTime();

        return timeA - timeB;
      },
      render: (_, record) => {
        const dataColeta = getDateColetaFromRecord(record);
        if (dataColeta && dataColeta !== "") {
          const [year, month, day] = dataColeta.split('-');
          const localDate = new Date(+year, +month - 1, +day); // Mês começa do 0
          return format(localDate, 'dd/MM/yyyy');
        }
        return "";
      },
    },
    {
      title: 'Código Frete',
      dataIndex: 'numberSerial',
      key: 'numberSerial',
      align: 'center',
      filteredValue: columnFilters['numberSerial'] ? [columnFilters['numberSerial']] : null,
      ...getColumnSearchProps('numberSerial', 'number'),
      sorter: (a, b) => {
        const idA = a.numberSerial?.toString() || "";
        const idB = b.numberSerial?.toString() || "";
        return idA.localeCompare(idB);
      },
      render: (value) => <span>{value}</span>,
    },
    {
      title: 'Nome Pagador',
      dataIndex: ['clientPayment', 'name'],
      key: 'nameClient',
      align: 'center',
      // width: '27%',
      filteredValue: columnFilters['clientPayment.name'] ? [columnFilters['clientPayment.name']] : null,
      ...getColumnSearchProps(['clientPayment', 'name'], 'text', 40),
      sorter: (a, b) => {
        const nameA = a.clientPayment?.name || '';
        const nameB = b.clientPayment?.name || '';
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: 'Nome Motorista',
      dataIndex: ['freight', 'getDriverFreight', 'name'],
      key: 'nameDriver', // camelCase correto
      align: 'center',
      filteredValue: columnFilters['freight.getDriverFreight.name'] ? [columnFilters['freight.getDriverFreight.name']] : null,
      ...getColumnSearchProps(['freight', 'getDriverFreight', 'name'], 'text', 40),
      sorter: (a, b) => {
        const nameA =
          a.freight?.getDriverFreight?.name ||
          a.getDriverFreight?.name ||
          a.driver?.name ||
          '';
        const nameB =
          b.freight?.getDriverFreight?.name ||
          b.getDriverFreight?.name ||
          b.driver?.name ||
          '';
        return nameA.localeCompare(nameB, 'pt-BR', { sensitivity: 'base' });
      },
    },
    {
      title: 'Origem',
      dataIndex: ['firstDelivery', 'city'],
      key: 'firstDeliveryCity', // camelCase
      align: 'center',
      filteredValue: columnFilters['firstDelivery.city'] ? [columnFilters['firstDelivery.city']] : null,
      ...getColumnSearchProps(['firstDelivery', 'city'], 'text', 40),
      sorter: (a, b) => {
        const cityA = a.firstDelivery?.city || '';
        const cityB = b.firstDelivery?.city || '';
        return cityA.localeCompare(cityB);
      },
    },
    {
      title: 'Destino',
      dataIndex: ['lastDelivery', 'city'],
      key: 'lastDeliveryCity', // camelCase
      align: 'center',
      filteredValue: columnFilters['lastDelivery.city'] ? [columnFilters['lastDelivery.city']] : null,
      ...getColumnSearchProps(['lastDelivery', 'city'], 'text', 40),
      sorter: (a, b) => {
        const cityA = a.lastDelivery?.city || '';
        const cityB = b.lastDelivery?.city || '';
        return cityA.localeCompare(cityB);
      },
    },
    {
      title: 'Valor do Frete',
      dataIndex: ['freight', 'valueFreightage'],
      key: 'freight',
      align: 'center',
      // width: '22%',
      filteredValue: columnFilters['freight.valueFreightage'] ? [columnFilters['freight.valueFreightage']] : null,
      ...getColumnSearchProps(['freight', 'valueFreightage'], 'money'),
      render: (valueFreightage) => (
        `${(valueFreightage || 0).toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}`
      ),
      sorter: (a, b) => {
        const valueA = a.freight?.valueFreightage || 0;
        const valueB = b.freight?.valueFreightage || 0;
        return valueA - valueB;
      },
    },
    {
      title: 'Pagando ao Motorista',
      dataIndex: ['driver', 'valueNegotiated'],
      key: 'valueNegotiated',
      align: 'center',
      // width: '30%',
      filteredValue: columnFilters['driver.valueNegotiated'] ? [columnFilters['driver.valueNegotiated']] : null,
      ...getColumnSearchProps(['driver', 'valueNegotiated'], 'money'),
      render: (driver) => (
        `${(driver || 0).toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}`
      ),
      sorter: (a, b) => {
        const valueA = a.driver?.valueInitial || 0;
        const valueB = b.driver?.valueInitial || 0;
        return valueA - valueB;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      filteredValue: columnFilters['status.describe'] ? columnFilters['status.describe'] : null,
      ...getStatusFilterProps(),
      render: (status) => {
        const describeText =
          status.describe === 'Pendente de validação'
            ? 'P. Validação'
            : status.describe === 'Pendente de contratação'
              ? 'P.Contratação'
              : status.describe;
        return (
          <Tooltip title={status.describe}>
            <div>
              {renderStatusColor(describeText)}
            </div>
          </Tooltip>
        );
      },
      sorter: (a, b) => a.status.describe.localeCompare(b.status.describe),
    },
    {
      title: 'Ações',
      align: 'center',
      key: 'action',
      render: (_, record) => {
        const handleMenuClick = ({ key }) => {
          if (key === 'queue') getDriversQueue(record.id);
          if (key === 'delete') deleteEmb(record.id);
        };

        const menu = (
          <Menu
            onClick={handleMenuClick}
            style={{ minWidth: 180, padding: '8px 0', borderRadius: 8 }}
          >
            <Menu.Item key="queue" style={{ padding: '10px 16px' }}>
              <Link to={`#${record.id}`} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <DriverBadge recordId={record.id} />
                Ver Fila
              </Link>
            </Menu.Item>

            <Menu.Item key="edit" style={{ padding: '10px 16px' }}>
              {
                user?.permissoes?.freight?.isEdit || user?.permissoes?.frete?.editar ? (
                  <Link to={`/freightCopy/${record.id}`} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <EditOutlined />
                    Editar
                  </Link>
                )
                  :
                  (
                    <Link to={`/freightCopy/${record.id}`} state={{ viewOnly: true }} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <VisibilityIcon />
                      Visualizar
                    </Link>
                  )
              }
            </Menu.Item>

            <Menu.Item key="copy" style={{ padding: '10px 16px' }}>
              <Link to={`/freightCopy/${record.id}?copy=true`} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CopyOutlined />
                Copiar
              </Link>
            </Menu.Item>

            <Menu.Item key="delete" style={{ padding: '10px 16px', color: 'red' }}>
              {
                user?.permissoes?.freight?.isDelete || user?.permissoes?.frete?.excluir ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <DeleteOutlined />
                    Excluir
                  </div>
                )
                  :
                  (
                    ""
                  )
              }
            </Menu.Item>
          </Menu>
        );

        return (
          <Dropdown overlay={menu} trigger={['click']} placement="bottomRight">
            <Button
              type="text"
              icon={<SmallDashOutlined style={{ fontSize: 20 }} />}
            />
          </Dropdown>
        );
      },
    }

  ];

  // Aplica todos os filtros e ordenação aos dados
  const filteredAndSortedData = useMemo(() => {
    let filteredData = [...datas];

    // Aplica filtro de data
    if (dateRange && dateRange.length === 2) {
      const [startDate, endDate] = dateRange;
      filteredData = filteredData.filter(record => {
        const dateColeta = getDateOnly(getDateColetaFromRecord(record));
        if (!dateColeta) return false;

        const start = startDate ? getDateOnly(startDate) : null;
        const end = endDate ? getDateOnly(endDate) : null;

        // Se ambas as datas são iguais, compara somente o dia
        if (start && end && start.getTime() === end.getTime()) {
          return dateColeta.getTime() === start.getTime();
        }

        // Somente data inicial definida: data em ou após startDate
        if (start && !end) {
          return dateColeta.getTime() >= start.getTime();
        }

        // Somente data final definida: data em ou antes de endDate
        if (!start && end) {
          return dateColeta.getTime() <= end.getTime();
        }

        // Intervalo completo entre startDate e endDate (inclusive)
        if (start && end) {
          return (
            dateColeta.getTime() >= start.getTime() &&
            dateColeta.getTime() <= end.getTime()
          );
        }

        return true;
      });
    }

    // Aplica filtros de colunas
    Object.keys(columnFilters).forEach(columnKey => {
      const filterValue = columnFilters[columnKey];
      if (filterValue == null || filterValue === '' || (Array.isArray(filterValue) && filterValue.length === 0)) return;

      filteredData = filteredData.filter(record => {
        // Filtro de status
        if (columnKey === 'status.describe') {
          if (!record.status?.describe) return false;

          if (Array.isArray(filterValue) && filterValue.length > 0) {
            // Comparação case-insensitive e normalizada
            const recordStatus = record.status.describe.trim();
            return filterValue.some(status => {
              const filterStatus = String(status).trim();
              return recordStatus === filterStatus || recordStatus.toLowerCase() === filterStatus.toLowerCase();
            });
          }

          if (filterValue) {
            const recordStatus = String(record.status.describe).trim();
            const filterStatus = String(filterValue).trim();
            return recordStatus === filterStatus || recordStatus.toLowerCase() === filterStatus.toLowerCase();
          }

          return false;
        }

        // Filtro de data já aplicado acima
        if (columnKey === 'dateColeta') return true;

        const columnKeyArray = columnKey.split('.');
        let recordValue = record;

        for (const key of columnKeyArray) {
          recordValue = recordValue?.[key];
        }

        if (recordValue == null) return false;

        // Filtros com operadores (para valores monetários)
        if (typeof filterValue === 'object' && filterValue.operator && filterValue.value !== undefined) {
          const { operator, value: filterVal } = filterValue;
          const recordNum = typeof recordValue === 'number' ? recordValue : moneyToInt(recordValue);
          const filterNum = typeof filterVal === 'number' ? filterVal : moneyToInt(filterVal);

          switch (operator) {
            case 'greaterThan':
              return recordNum > filterNum;
            case 'greaterThanOrEqual':
              return recordNum >= filterNum;
            case 'lessThan':
              return recordNum < filterNum;
            case 'lessThanOrEqual':
              return recordNum <= filterNum;
            case 'equals':
              return recordNum === filterNum;
            case 'notEquals':
              return recordNum !== filterNum;
            default:
              return true;
          }
        }

        // Filtros simples de texto/número
        const isNumber = columnKey === 'numberSerial';
        const isMoney = columnKey === 'freight.valueFreightage' || columnKey === 'driver.valueNegotiated';

        if (isNumber) {
          return String(recordValue).includes(String(filterValue));
        }
        else if (isMoney) {
          const filterInt = typeof filterValue === 'number' ? filterValue : moneyToInt(filterValue);
          const recordInt = typeof recordValue === 'number' ? Math.round(recordValue) : moneyToInt(recordValue);
          return String(recordInt).includes(String(filterInt));
        }
        else {
          return recordValue.toString().toLowerCase().includes(filterValue.toString().toLowerCase());
        }
      });
    });

    // Aplica ordenação por prioridade de status
    const sortedData = filteredData.sort((a, b) => {
      const priorityA = statusPriority[a.status?.describe] || 5;
      const priorityB = statusPriority[b.status?.describe] || 5;
      return priorityA - priorityB;
    });

    return sortedData;
  }, [datas, dateRange, columnFilters, getDateColetaFromRecord]);

  // Resetar página quando filtros mudarem
  useEffect(() => {
    setCurrent(1);
  }, [dateRange, columnFilters]);

  return (
    <>
      <DialogFileDriver driversInFila={driversInFila} openModal={openModal} handleClose={handleClose} />
      <ModalFilter
        visible={openModalFilter}
        onClose={() => setOpenModalFilter(false)}
        onApply={handleApplyCustomFilter}
      />
      <div className="table-wrapper-with-actions">
        <Table
          columns={columns}
          dataSource={filteredAndSortedData}
          rowKey="id"
          loading={loading}
          pagination={{
            current,
            pageSize,
            total: filteredAndSortedData.length,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            hideOnSinglePage: false,
            onChange: (page, size) => {
              setCurrent(page);
              setPageSize(size);
            },
          }}
        />
      </div>
    </>
  );
};

export default DataFreteTable;